import type { LiveFeedPhase } from '@repo/types';
import { MatchStatus } from '@repo/types';

const EXTRA_TIME_STATUSES: MatchStatus[] = [
  MatchStatus.FirstHalfOfExtraTime,
  MatchStatus.HalfTimeInExtraTime,
  MatchStatus.SecondHalfOfExtraTime,
  MatchStatus.FullTimeAfterExtraTime,
  MatchStatus.PenaltyShootOut,
];

export const matchHadExtraTime = (status: MatchStatus): boolean => EXTRA_TIME_STATUSES.includes(status);

/**
 * Formats the match minute to display stoppage/added time correctly.
 *
 * Logic:
 * - FirstHalf: If minute > 45, show "45+X'"
 * - SecondHalf: If minute > 90, show "90+X'"
 * - FirstHalfOfExtraTime: If minute > 105, show "105+X'"
 * - SecondHalfOfExtraTime: If minute > 120, show "120+X'"
 * - HalfTime: Returns undefined (should show "HT" label instead)
 * - FullTime: Returns undefined (match is over)
 *
 * @param status - The current match status/phase
 * @param currentMinute - The current minute string in format "MM:SS" (e.g., "47:15")
 * @returns Formatted minute string (e.g., "45+2'") or undefined if not applicable
 */
export const formatMatchMinute = (status: MatchStatus, currentMinute?: string): string | undefined => {
  if (!currentMinute) {
    return undefined;
  }

  const minuteStr = currentMinute.split(':')[0]!;
  const minute = parseInt(minuteStr, 10);

  if (isNaN(minute)) {
    return undefined;
  }

  switch (status) {
    case MatchStatus.FirstHalf:
      if (minute > 45) {
        const stoppageTime = minute - 45;
        return `45+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.SecondHalf:
      if (minute > 90) {
        const stoppageTime = minute - 90;
        return `90+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.FirstHalfOfExtraTime:
      if (minute > 105) {
        const stoppageTime = minute - 105;
        return `105+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.SecondHalfOfExtraTime:
      if (minute > 120) {
        const stoppageTime = minute - 120;
        return `120+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.HalfTime:
    case MatchStatus.HalfTimeInExtraTime:
      // Don't show minute during half time breaks - UI shows "HT" label
      return undefined;

    case MatchStatus.FullTime:
    case MatchStatus.FullTimeAfterExtraTime:
      // Match is over, no minute to display
      return undefined;

    case MatchStatus.PenaltyShootOut:
      // During penalties, no regular minute display
      return undefined;

    case MatchStatus.Live:
      // Streamline-specific live status - show minute as is
      return `${minute}`;

    default:
      return `${minute}`;
  }
};

/**
 * Formats a minute for display when the event phase is not available (e.g., completed match data).
 * Infers added/stoppage time from the minute value and whether the match had extra time.
 *
 * For standard matches (no extra time):
 * - minute > 90 → "90+X" (second half added time)
 *
 * For extra time matches:
 * - minutes 91-120 are regular extra time → shown as-is
 * - minute > 120 → "120+X" (second half of extra time added time)
 *
 * @param minute - The event minute as a number
 * @param matchHadExtraTime - Whether the match went to extra time (default: false)
 * @returns Formatted minute string (e.g., "90+4" or "120+3")
 */
export const formatMinuteDisplay = (minute: number, matchHadExtraTime: boolean = false): string => {
  if (matchHadExtraTime) {
    if (minute > 120) {
      return `120+${minute - 120}`;
    }
    return `${minute}`;
  }

  if (minute > 90) {
    return `90+${minute - 90}`;
  }

  return `${minute}`;
};

/**
 * Formats an event minute to display stoppage/added time correctly.
 * Used for LiveFeed events where the minute and phase are stored directly.
 *
 * @param minute - The event minute as a number
 * @param phase - The match phase when the event occurred (optional)
 * @returns Formatted minute string (e.g., "45+1") or just the minute if no stoppage time
 */
export const formatEventMinute = (minute: number, phase?: MatchStatus): string => {
  if (phase === undefined) {
    return `${minute}`;
  }

  switch (phase) {
    case MatchStatus.FirstHalf:
      if (minute > 45) {
        const stoppageTime = minute - 45;
        return `45+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.SecondHalf:
      if (minute > 90) {
        const stoppageTime = minute - 90;
        return `90+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.FirstHalfOfExtraTime:
      if (minute > 105) {
        const stoppageTime = minute - 105;
        return `105+${stoppageTime}`;
      }
      return `${minute}`;

    case MatchStatus.SecondHalfOfExtraTime:
      if (minute > 120) {
        const stoppageTime = minute - 120;
        return `120+${stoppageTime}`;
      }
      return `${minute}`;

    default:
      return `${minute}`;
  }
};

/**
 * Formats an event minute, preferring the phase the event was actually recorded
 * in and falling back to inference from the final match status when it has none.
 *
 * The phase is what makes first-half added time expressible at all: a bare
 * minute 48 is indistinguishable from a genuine 48th-minute second-half event,
 * so `formatMinuteDisplay` can only ever infer added time past 90 (or past 120).
 * With the phase present, a FirstHalf minute 48 correctly reads "45+3". Data
 * predating phase tracking (imported historical matches, the minutes-only card
 * arrays on a lineup) carries none, hence the fallback rather than a hard
 * requirement.
 *
 * `phase` is `LiveFeedPhase` rather than the wider `MatchStatus` on purpose:
 * only the four phases play actually happens in can carry an event, and the
 * wider type would let `HalfTime`/`FullTime`/`PenaltyShootOut` type-check their
 * way into `formatEventMinute`'s `default:` branch, returning the raw minute
 * with no signal that the phase was meaningless. (`formatEventMinute` itself
 * stays `MatchStatus` — `LiveScore` passes a live match *status* to it.)
 *
 * @param minute - The event minute as a number
 * @param phase - The match phase the event occurred in, when recorded
 * @param hasExtraTime - Whether the match went to extra time (see matchHadExtraTime)
 */
export const formatPhaseAwareMinute = (
  minute: number,
  phase: LiveFeedPhase | undefined,
  hasExtraTime: boolean
): string => (phase ? formatEventMinute(minute, phase) : formatMinuteDisplay(minute, hasExtraTime));
